import { NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import routes from "../../../routes";
import ExitIcon from "../../icons/ExitIcon";
import useLogout from "../../../hooks/useLogout";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  getRoleSidebarConfig,
  isTabAllowedForRole,
} from "../../../config/sidebarRoles";

/**
 * `DropdownPortal` rendered via React Portal so it visually escapes the
 * sidebar container's overflow/z-index trap. Defined OUTSIDE the
 * SideBar component (was previously inside, recreated per render —
 * if its body ever held state, that state would reset on every
 * parent re-render).
 */
const DropdownPortal = ({ open, dropdownRef, clinics, onPick }) => {
  if (!open) return null;
  return createPortal(
    <div
      ref={dropdownRef}
      className="fixed z-[9999] md:w-[200px] w-[180px]"
      style={{ top: 20, left: 200 }}
      role="menu"
      aria-label="Switch clinic"
    >
      {/* Arrow pointing left */}
      <div className="absolute left-0 top-4 transform -translate-x-full">
        <div className="w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-r-[8px] border-r-white"></div>
      </div>
      <div className="max-h-60 overflow-y-auto rounded-lg shadow-lg">
        {clinics.map((lab) => (
          <button
            key={lab.id || lab.name}
            type="button"
            role="menuitem"
            className="w-full text-left px-4 py-3 hover:bg-gray-200 transition-colors duration-150 border-b bg-white border-gray-100 last:border-b-0 first:rounded-t-lg last:rounded-b-lg"
            onClick={() => onPick(lab)}
          >
            <span className="text-sm text-gray-700">{lab.name}</span>
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
};

const SideBar = ({
  // eslint-disable-next-line no-unused-vars
  sidebarOpen = true,
  // eslint-disable-next-line no-unused-vars
  setSidebarOpen,
  handleSwitchClinic,
  collapse,
}) => {
  const location = useLocation();
  // Narrow selectors — was subscribing to entire `state.hospitalInfo`
  // (never read) and entire `state.auth` (re-render trigger on any
  // unrelated field).
  const role = useSelector((state) => state.auth?.role);
  const variant = useSelector((state) => state.auth?.variant);
  const linkedClinics = useSelector(
    (state) => state.auth?.linked_clinics ?? [],
  );
  const activeClinic = useSelector((state) => state.auth?.active_clinic);

  const { pathname } = location;
  const logout = useLogout();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  // Close dropdown on outside click OR Escape — touch flows on iOS
  // Safari sometimes don't synthesize mousedown, so listen for both.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isOpen]);

  const handleTriggerClick = () => setIsOpen((v) => !v);

  const handleLabSelect = (lab) => {
    handleSwitchClinic(lab.id);
    setIsOpen(false);
  };

  // Normalize a path for comparison: strip trailing slashes and lower-
  // case so route matching tolerates both "/foo/" vs "/foo" and
  // accidental capitalisation in the routes table.
  const normalizePath = (p = "") => p.replace(/\/+$/, "").toLowerCase() || "/";

  const normalizedPath = normalizePath(pathname);

  // Active when the current path either equals the route's path OR
  // is a child of it. Child match is by PREFIX so deep links
  // (e.g. /prescription_details/abc) still highlight the parent —
  // previously this used `Array.includes(pathname)` which required
  // an exact match.
  const isActive = (route) => {
    const routePath = normalizePath(route.path);
    if (normalizedPath === routePath) return true;
    if (Array.isArray(route.childrens)) {
      return route.childrens.some((child) => {
        const childPath = normalizePath(child);
        return (
          normalizedPath === childPath ||
          normalizedPath.startsWith(`${childPath}/`)
        );
      });
    }
    return false;
  };

  // Pre-filter the route list once per render. Two-step:
  //   1. Drop non-dashboard routes.
  //   2. Drop routes the current role isn't allowed to see, applying
  //      tier-based gating (Inventory needs Pro, Analytics needs
  //      Owner) consistently for BOTH config-driven roles AND
  //      free-form roles. Previous version short-circuited the
  //      tier guards for any role with a `ROLE_SIDEBAR_CONFIG` entry,
  //      bypassing tenant boundaries.
  const visibleRoutes = useMemo(() => {
    const isProUser = typeof variant === "string" && variant.startsWith("Pro");
    const isOwner = typeof role === "string" && role.startsWith("Owner");
    return routes
      .filter((route) => route.dashboardItem)
      .filter((route) => {
        const roleConfig = getRoleSidebarConfig(role);
        const allowed = isTabAllowedForRole(
          route.title,
          { role, variant },
          roleConfig,
        );
        if (allowed === false) return false;
        // Apply tier gates UNCONDITIONALLY (not only when there's no
        // role config), so non-Pro / non-Owner users on configured
        // roles can't sneak past via the role-config short-circuit.
        if (route.title === "Inventory" && !isProUser) return false;
        if (route.title === "Analytics" && !isOwner) return false;
        return true;
      });
  }, [role, variant]);

  return (
    // `z-[9999]` (Tailwind arbitrary syntax). Was `z-9999` previously
    // — Tailwind silently dropped that as an invalid utility, so the
    // sidebar had no z-index and could render under unrelated stacked
    // elements.
    <aside
      aria-label="Primary navigation"
      className={`left-0 top-0 z-[9999] flex min-h-screen flex-col bg-gradient-primary duration-100 ease-linear lg:translate-x-0 ${
        collapse ? "w-[69.8px]" : "w-[220px]"
      }`}
    >
      {/* SIDEBAR HEADER */}
      <div className="flex items-center justify-center mt-[21px] mb-[29.25px] -ml-[5px]">
        {collapse ? (
          <NavLink to="/">
            <div className="image-container">
              <img
                style={{
                  width: !collapse ? "143px" : "28px",
                  height: !collapse ? "45px" : "45px",
                  transition: "width 0.2s ease-in-out, height 0.5s ease-in-out",
                }}
                src={!collapse ? "/icons/BigLogo.png" : "/icons/SmallLogo.svg"}
                alt=""
              />
            </div>
          </NavLink>
        ) : (
          <div className="space-y-2">
            {/* Clickable Hospital Company Name. Now a real <button>
                so it's keyboard-accessible (Tab + Enter / Space) and
                announces itself with the right semantics. */}
            <button
              ref={triggerRef}
              type="button"
              aria-haspopup="menu"
              aria-expanded={isOpen}
              className="font-semibold text-center max-w-[190px] mx-auto truncate cursor-pointer bg-gray-100 select-none p-2 rounded-xl transition-colors duration-200"
              onClick={handleTriggerClick}
            >
              {activeClinic || "—"}
            </button>
            <DropdownPortal
              open={isOpen}
              dropdownRef={dropdownRef}
              clinics={linkedClinics}
              onPick={handleLabSelect}
            />
          </div>
        )}
      </div>
      {/* SIDEBAR HEADER END */}

      <div className="no-scrollbar flex flex-col min-h-fit duration-200 ease-linear">
        <nav aria-label="Application sections">
          <ul className="mb-6 flex justify-center items-center -ml-[5px] flex-col gap-1.5">
            {visibleRoutes.map((route) => {
              const active = isActive(route);
              return (
                // Stable key (route.path) so React doesn't remount on
                // reorder.
                <div key={route.path} className="flex">
                  <div className="relative">
                    <li>
                      <NavLink
                        to={route.path}
                        aria-current={active ? "page" : undefined}
                        className={`group relative flex items-center gap-2.5 rounded-[15px] font-medium text-bodydark1 duration-200 ease-in-out ${
                          collapse
                            ? "w-[58px] h-[54px] justify-center"
                            : "w-[200px] h-[54px] px-5"
                        } ${active ? "bg-white bg-opacity-[26%]" : ""}`}
                      >
                        {route.icon}
                        {!collapse && (
                          <span className="text-white text-heading2R pt-1">
                            {route.title}
                          </span>
                        )}
                      </NavLink>
                    </li>
                  </div>
                  {active && (
                    <div className="border-l-4 h-[54px] rounded-tl-md rounded-tr-none rounded-bl-md rounded-br-none absolute right-[0.9px] border-white" />
                  )}
                </div>
              );
            })}

            {/* Mobile-only Logout. Real <button> for keyboard
                accessibility — was a bare <div onClick>. */}
            <div className="mt-2 flex md:hidden">
              <div className="relative">
                <li>
                  <button
                    type="button"
                    onClick={logout}
                    className="group relative flex justify-start items-center gap-2.5 rounded-[15px] font-medium text-bodydark1 duration-200 ease-in-out cursor-pointer w-[200px] h-[54px] px-5 hover:bg-white hover:bg-opacity-[26%]"
                  >
                    <div className="mt-0.5">
                      <ExitIcon />
                    </div>
                    <span className="text-white text-heading2R pt-1">
                      Logout
                    </span>
                  </button>
                </li>
              </div>
            </div>
          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default SideBar;
